import React, { useState, useEffect } from "react";
import config from "../../config";

const SendEmail = () => {
  const [recipientType, setRecipientType] = useState("single");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [subscriptions, setSubscriptions] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg(""); // Clear message after 3 seconds
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);
  useEffect(() => {
    if (recipientType !== "single") {
      const fetchSubscriptions = async () => {
        setStatus("Loading...");
        try {
          const response = await fetch(`${config.backendUrl}/api/get_subscriptions`);
          const data = await response.json();

          if (response.ok) {
            console.log("Fetched data:", data); // Log the fetched data

            let filteredSubscriptions = [];

            // Log the current recipientType and the available roles in data
            console.log("Recipient Type:", recipientType);

            // Check the roles of the subscriptions returned from the backend
            const roles = data.map(sub => sub.role);
            console.log("Roles in subscriptions data:", roles);

            // Filter subscriptions based on the selected recipient type
            if (recipientType === "all") {
              filteredSubscriptions = data;  // Get all subscriptions
            } else {
              filteredSubscriptions = data.filter(
                (subscription) => subscription.role && subscription.role.toLowerCase() === recipientType
              );
            }

            console.log("Filtered subscriptions:", filteredSubscriptions); // Log the filtered subscriptions

            setSubscriptions(filteredSubscriptions);
            setStatus("");
          } else {
            setStatus("Failed to load subscriptions.");
          }
        } catch (error) {
          setStatus("Failed to load subscriptions.");
        }
      };

      fetchSubscriptions();
    } else {
      setSubscriptions([]);
    }
  }, [recipientType]);

  const validateForm = () => {
    if (recipientType === "single" && !email) {
      setStatus("Please provide a valid email address.");
      return false;
    }
    if (!subject || !message) {
      setStatus("Subject and message cannot be empty.");
      return false;
    }
    return true;
  };

  const handleSendEmail = async () => {
    if (!validateForm()) return;
  
    setStatus("Sending...");
    
    const payload = {
      recipientType,
      email: recipientType === "single" ? email : undefined, 
      emails: recipientType !== "single" ? subscriptions.map(sub => sub.email) : undefined, // Include multiple emails
      subject,
      message,
    };
  
    try {
      const response = await fetch(`${config.backendUrl}/api/send_email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to send email");

      setStatus("");
      setSuccessMsg("✅ Email sent successfully!");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (error) {
      setStatus(`❌ Failed to send email. Error: ${error.message}`);
    }
  };
  

  return (
    <div className="container mt-5 d-flex justify-content-center">
      <div className="w-100" style={{ maxWidth: "600px" }}>
        <div className="text-center mb-4">
          <h2 className="font-weight-bold" style={{ fontSize: '2rem', color: '#333' }}>
            Email Sender
          </h2>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            Fill out the form below to send an email to the desired recipients.
          </p>
        </div>

        <div className="bg-gradient shadow-lg rounded-4 p-5 w-100" style={{ background: 'linear-gradient(135deg, #6a11cb, #2575fc)' }}>
          
          {/* Recipient Type Selector */}
          <div className="mb-4">
            <label className="form-label text-white" style={{ fontWeight: '600' }}>Recipient Type</label>
            <select
              className="form-select shadow-sm rounded-3"
              onChange={(e) => setRecipientType(e.target.value)}
              value={recipientType}
              style={{
                backgroundColor: '#fff',
                border: 'none',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              }}
            >
              <option value="single">Single User</option>
              <option value="parent">Parents</option>
              <option value="teacher">Teachers</option>
              <option value="student">Students</option>
              <option value="all">All (Parents, Teachers, Students)</option>
            </select>
          </div>

          {/* Email input for Single Recipient */}
          {recipientType === "single" && (
            <div className="mb-4">
              <label className="form-label text-white" style={{ fontWeight: '600' }}>Email</label>
              <input
                type="email"
                className="form-control shadow-sm rounded-3"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  backgroundColor: '#fff',
                  border: 'none',
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                }}
              />
            </div>
          )}

          {/* Subscriptions List (for Parent, Teacher, Student, and All) */}
          {recipientType !== "single" && subscriptions.length > 0 && (
            <div className="mb-4">
              <label className="form-label text-white" style={{ fontWeight: '600' }}>Available Subscriptions</label>
              <ul className="list-group shadow-sm rounded-3" style={{ backgroundColor: '#fff' }}>
                {subscriptions.map((subscription) => (
                  <li
                    className="list-group-item d-flex justify-content-between align-items-center"
                    key={subscription.id}
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: 'none',
                      boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {subscription.email} ({subscription.role})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* No Subscriptions Found Message */}
          {recipientType !== "single" && subscriptions.length === 0 && (
            <p className="text-white">No subscriptions found for the selected role.</p>
          )}

          {/* Subject input */}
          <div className="mb-4">
            <label className="form-label text-white" style={{ fontWeight: '600' }}>Subject</label>
            <input
              type="text"
              className="form-control shadow-sm rounded-3"
              placeholder="Enter subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              style={{
                backgroundColor: '#fff',
                border: 'none',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              }}
            />
          </div>

          {/* Message Textarea */}
          <div className="mb-4">
            <label className="form-label text-white" style={{ fontWeight: '600' }}>Message</label>
            <textarea
              className="form-control shadow-sm rounded-3"
              rows="4"
              placeholder="Enter message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              style={{
                backgroundColor: '#fff',
                border: 'none',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              }}
            />
          </div>

          {/* Send Email Button */}
          <div className="d-flex justify-content-end mb-4">
            <button
              className="btn btn-lg shadow-sm px-4 py-2 rounded-3"
              style={{
                backgroundColor: '#6a11cb',
                color: '#fff',
                border: 'none',
                transition: 'all 0.3s ease',
              }}
              onClick={handleSendEmail}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2575fc'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#6a11cb'}
            >
              Send Email
            </button>
          </div>

          {/* Status Message */}
          {status && <p className="text-center text-white">{status}</p>}
          {status && <p className="text-center text-warning">{status}</p>}
          {successMsg && <p className="text-center text-success">{successMsg}</p>}
        </div>
      </div>
    </div>
  );
};

export default SendEmail;
